import { createBrowserRouter, Navigate } from 'react-router';
import {
  CalendarClock,
  Rabbit,
  Phone,
  ClipboardPenLine,
  UserSearch,
  Workflow,
  Book,
  Bot,
  ChartBar,
  Target,
  Wrench,
  Mic,
  FlaskConical,
  PhoneCall,
  IdCard,
  ScrollText,
  Stamp,
  History,
  FileText,
  Settings,
  Cog,
} from 'lucide-react';

import { ProtectedRoute } from '@/components/layout/ProtectedRoute'
import { Layout } from '@/components/layout/Layout'
import { Login } from '@/pages/login/Login'
import { Dashboard } from '@/pages/dashboard/Dashboard'
import { Realtime } from '@/pages/realtime/Realtime'
import { HistoryPage } from '@/pages/history'
import { Reports } from '@/pages/reports/Reports'
import { QAPage } from '@/pages/qa'
import { CampaignsPage } from '@/pages/campaigns'
import { ScriptsPage, ScriptEditor } from '@/pages/scripts'
import { ToolsPage } from '@/pages/tools'
import { VoicesPage, VoiceEditor } from '@/pages/voices'
import { ListsPage } from '@/pages/lists'
import { LeadSearchPage } from '@/pages/lead-search'
import { ApiToken } from '@/pages/api-token/ApiToken'
import { ApiDocs } from '@/pages/api-docs/ApiDocs'
import { ChangeHistory } from '@/pages/change-history/ChangeHistory'
import { AgentEditorPage } from '@/pages/agent-page';
import { SimulationsPage } from '@/pages/simulations';
import { AgentsPage } from '@/pages/agents';
import { DemoPage } from '@/pages/demo/DemoPage';
import { OrganizationSettingsPage } from '@/pages/organization-settings/OrganizationSettingsPage';
import { ToolTypesAdminPage } from '@/pages/tool-types-admin';
import { SimulationSettingsPage } from '@/pages/simulation-settings';

// Dialer pages
import { DialerPage } from '@/pages/dialer'
import { DialerStatus } from '@/pages/dialer/Status'
import { DialerSettings } from '@/pages/dialer/Settings'
import { DialerCallLogs } from '@/pages/dialer/CallLogs'
import { CallerIds } from '@/pages/dialer/CallerIds'
import DialerAdmin from '@/pages/dialer/admin/Admin'
import InstanceTasks from '@/pages/dialer/InstanceTasks'

import { ROUTES } from '@/shared/config';
import {TwilioNumbersPage} from "@/pages/twilio-numbers";

export const router = createBrowserRouter([
  {
    path: `${ROUTES.LOGIN}/*`,
    element: <Login />,
  },
  {
    path: ROUTES.DEMO,
    element: <DemoPage />,
  },
  {
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to={ROUTES.DASHBOARD} replace />,
      },
      // MONITORING
      {
        path: ROUTES.DASHBOARD,
        element: <Dashboard />,
        handle: {
          kind: 'navigation',
          name: 'Daily Reports',
          href: '/dashboard',
          icon: <CalendarClock />,
          parent: 'Monitoring',
        }
      },
      {
        path: ROUTES.REALTIME,
        element: <Realtime />,
        handle: {
          kind: 'navigation',
          name: 'Real-time Data',
          href: '/realtime',
          icon: <Rabbit />,
          parent: 'Monitoring',
        }
      },
      {
        path: ROUTES.HISTORY,
        element: <HistoryPage />,
        handle: {
          kind: 'navigation',
          name: 'Conversations',
          href: '/history',
          icon: <Phone />,
          parent: 'Monitoring',
        }
      },
      {
        path: ROUTES.HISTORY_CALL,
        element: <HistoryPage />,
      },
      {
        path: ROUTES.REPORTS,
        element: <Reports />,
        handle: {
          kind: 'navigation',
          name: 'Custom Reports',
          href: '/reports',
          icon: <ChartBar />,
          parent: 'Monitoring',
        }
      },
      {
        path: ROUTES.QA,
        element: <QAPage />,
        handle: {
          kind: 'navigation',
          name: 'Quality Assurance',
          href: '/qa',
          icon: <ClipboardPenLine />,
          parent: 'Monitoring',
        }
      },
      {
        path: ROUTES.QA_CALL,
        element: <QAPage />,
      },
      // AI SETTINGS
      {
        path: ROUTES.CAMPAIGNS,
        element: <CampaignsPage />,
        handle: {
          kind: 'navigation',
          name: 'Campaign Editor',
          href: '/campaigns',
          icon: <Target />,
          parent: 'AI Settings',
        }
      },
      {
        path: ROUTES.SCRIPTS.LIST,
        element: <ScriptsPage />,
        handle: {
          kind: 'navigation',
          name: 'LLMs',
          href: '/scripts',
          icon: <Book />,
          parent: 'AI Settings',
        },
        children: [
          {
            path: ':id?',
            element: <ScriptEditor />,
          },
        ],
      },
      {
        path: ROUTES.TOOLS,
        element: <ToolsPage />,
        handle: {
          kind: 'navigation',
          name: 'Tools',
          href: '/tools',
          icon: <Wrench />,
          parent: 'AI Settings',
        }
      },
      {
        path: ROUTES.VOICES.LIST,
        element: <VoicesPage />,
        handle: {
          kind: 'navigation',
          name: 'Voices',
          href: '/voices',
          icon: <Mic />,
          parent: 'AI Settings',
        },
        children: [
          {
            path: ':id?',
            element: <VoiceEditor />,
          },
        ],
      },
      {
        path: ROUTES.SIMULATIONS,
        element: <SimulationsPage />,
        handle: {
          kind: 'navigation',
          name: 'Simulations',
          href: '/simulations',
          icon: <FlaskConical />,
          parent: 'AI Settings',
        }
      },
      // DIALING
      {
        path: ROUTES.DIALER,
        element: <DialerPage />,
        handle: {
          kind: 'navigation',
          name: 'Live Dialing',
          href: '/dialer',
          icon: <PhoneCall />,
          roles: ['admin', 'manager'],
          parent: 'Dialing',
          exact: true,
        }
      },
      {
        path: ROUTES.DIALER_STATUS,
        element: <DialerStatus />,
      },
      {
        path: ROUTES.DIALER_SETTINGS,
        element: <DialerSettings />,
      },
      {
        path: ROUTES.DIALER_ADMIN,
        element: <DialerAdmin />,
      },
      {
        path: ROUTES.DIALER_TASKS,
        element: <InstanceTasks />,
      },
      {
        path: ROUTES.DIALER_CALLER_IDS,
        element: <CallerIds />,
        handle: {
          kind: 'navigation',
          name: 'Phone Numbers',
          href: '/dialer/callerids',
          icon: <IdCard />,
          roles: ['admin', 'manager'],
          parent: 'Dialing',
        }
      },
      {
        path: ROUTES.DIALER_CALL_LOGS,
        element: <DialerCallLogs />,
        handle: {
          kind: 'navigation',
          name: 'Dialer Call Logs',
          href: '/dialer/call-logs',
          icon: <ScrollText />,
          parent: 'More',
        }
      },
      {
        path: ROUTES.LISTS,
        element: <ListsPage />,
        handle: {
          kind: 'navigation',
          name: 'Lists',
          href: '/lists',
          icon: <Workflow />,
          parent: 'Dialing',
        }
      },
      {
        path: ROUTES.LEAD_SEARCH,
        element: <LeadSearchPage />,
        handle: {
          kind: 'navigation',
          name: 'Leads',
          href: '/lead_search',
          icon: <UserSearch />,
          parent: 'Dialing',
        }
      },
      // MORE
      {
        path: ROUTES.API_TOKEN,
        element: <ApiToken />,
        handle: {
          kind: 'navigation',
          name: 'API Token',
          href: '/api-token',
          icon: <Stamp />,
          parent: 'More',
        }
      },
      {
        path: ROUTES.API_DOCS,
        element: <ApiDocs />,
        handle: {
          kind: 'navigation',
          name: 'API Docs',
          href: '/api-docs',
          icon: <FileText />,
          parent: 'More',
        }
      },
      {
        path: ROUTES.CHANGE_HISTORY,
        element: <ChangeHistory />,
        handle: {
          kind: 'navigation',
          name: 'Change History',
          href: '/change-history',
          icon: <History />,
          parent: 'More',
        }
      },
      {
        path: ROUTES.ORGANIZATION_SETTINGS,
        element: <OrganizationSettingsPage />,
        handle: {
          kind: 'navigation',
          name: 'Organization Settings',
          href: '/organization-settings',
          icon: <Settings />,
          parent: 'More',
        }
      },
      {
        path: ROUTES.TOOL_TYPES_ADMIN,
        element: <ToolTypesAdminPage />,
        handle: {
          kind: 'navigation',
          name: 'Tool Types',
          href: '/tool-types-admin',
          icon: <Cog />,
          parent: 'AI Settings',
          superadminOnly: true,
        }
      },
      {
        path: ROUTES.TWILIO_NUMBERS,
        element: <TwilioNumbersPage />,
        handle: {
          kind: 'navigation',
          name: 'Twilio Numbers',
          href: '/twilio-numbers',
          icon: <Phone />,
          parent: 'Admin',
          superadminOnly: true,
        }
      },
      {
        path: ROUTES.SIMULATION_SETTINGS,
        element: <SimulationSettingsPage />,
        handle: {
          kind: 'navigation',
          name: 'Simulation Settings',
          href: '/simulation-settings',
          icon: <Cog />,
          parent: 'Admin',
          superadminOnly: true,
        }
      },
      // Agents
      {
        path: ROUTES.AGENTS,
        element: <AgentsPage />,
        handle: {
          kind: 'navigation',
          name: 'Agents',
          href: '/agents',
          icon: <Bot />,
          parent: 'AI Settings',
        }
      },
      {
        path: ROUTES.AGENT_PAGE,
        element: <AgentEditorPage />,
      },
      // Catch-all
      {
        path: '*',
        element: <Navigate to={ROUTES.DASHBOARD} replace />,
      },
    ],
  },
])